import React, { useState, useEffect } from 'react';
import { fnOrUndef } from '../../../utils/funcUtils';
import { OrderingButtonGroup } from '../../../components/OrderingElements';
import sessionTimelineService from '../../../services/sessionTimelineService';
import alertService from '../../../services/AlertService';
import {
  SegmentContainer,
  EditFlexOptionToolbar,
  FlexSegmentOptionHeader,
  SegmentLeader,
  TimelineVerticalSpacer,
} from './SessionTimelineContentElements';
import { ButtonGroup, SmallRoundedIconButton } from '../../../components/Buttons';
import { useUser } from '../../authentication';
import { updateItemInList } from '../../../services/sessionTimelineService';
import useSessionTimeline from '../hooks/useSessionTimeline';
import { Segment, AdminSection } from '../models/timeline';

type SegmentData = {
  isOrdering: boolean;
  hasOrderChanged: boolean;
  isSaving: boolean;
  previousOptions: Segment[];
  options: Segment[];
};

const FlexOption = ({
  option,
  index,
  openModal,
  children,
  segment,
  segmentData,
  setSegmentData,
  timelineId,
}: {
  option: Segment;
  index: number;
  openModal: (modal: string, params: any) => void;
  children: React.ReactNode;
  segment: Segment;
  segmentData: SegmentData;
  setSegmentData: React.Dispatch<React.SetStateAction<SegmentData>>;
  timelineId: string;
}) => {
  const { user } = useUser();
  const { setTimeline } = useSessionTimeline();

  const updateItemIndex = (list: Segment[], fromIndex: number, toIndex: number) => {
    const listCopy = [...list];
    listCopy.splice(toIndex, 0, listCopy.splice(fromIndex, 1)[0]);
    return listCopy;
  };

  const updateFlexItemIndex = (from: number, to: number) => {
    const reordered = updateItemIndex(segmentData.options, from, to);
    setSegmentData(prev => ({
      ...prev,
      hasOrderChanged: true,
      options: reordered,
    }));
  };

  const updateFlexOptionOrder = () => {
    setSegmentData(prev => ({
      ...prev,
      isSaving: segmentData.hasOrderChanged,
      isOrdering: false,
    }));

    if (segmentData.hasOrderChanged) {
      sessionTimelineService
        .reorderSegments(
          timelineId,
          segmentData.options.map(s => s.segmentId),
          user?.userId
        )
        .then(() => alertService.show('Flex Options Updated'))
        .then(() => {
          setSegmentData(prev => ({
            ...prev,
            hasOrderChanged: false,
            isSaving: false,
            previousOptions: segmentData.options,
          }));
        })
        .then(() => {
          const updatedSegment = { ...segment, options: segmentData.options };
          setTimeline(previousTimeline => {
            const updatedTimeline = {
              ...previousTimeline,
              segments: updateItemInList(
                previousTimeline.segments,
                'segmentId',
                segment.segmentId,
                (segment: Segment) => ({
                  ...segment,
                  ...updatedSegment,
                })
              ),
            };
            return updatedTimeline;
          });
        })
        .catch(error => {
          alertService.showError();
          setSegmentData(prev => ({
            ...prev,
            hasOrderChanged: false,
            isSaving: false,
            options: segmentData.previousOptions,
          }));
        });
    }
  };

  return (
    <>
      <SegmentContainer id={`contents-${option.segmentId}`}>
        <FlexSegmentOptionHeader name={option.name} activity={option.activity || ''} position={index + 1} />
        <EditFlexOptionToolbar
          leftContent={null}
          rightContent={
            <ButtonGroup>
              {segmentData.isOrdering ? (
                <OrderingButtonGroup
                  horizontal={false}
                  save={updateFlexOptionOrder}
                  isSaving={segmentData.isSaving}
                  decrement={fnOrUndef(index > 0, () => updateFlexItemIndex(index, index - 1))}
                  increment={fnOrUndef(index < segmentData.options?.length - 1, () =>
                    updateFlexItemIndex(index, index + 1)
                  )}
                />
              ) : (
                <>
                  <SmallRoundedIconButton
                    onClick={() =>
                      setSegmentData(prev => ({
                        ...prev,
                        isOrdering: true,
                      }))
                    }
                  >
                    <i className="fas fa-bars"></i>
                  </SmallRoundedIconButton>
                  <SmallRoundedIconButton onClick={() => openModal('editSegment', { index, segment: option })}>
                    <i className="fas fa-pen"></i>
                  </SmallRoundedIconButton>
                  <SmallRoundedIconButton onClick={() => openModal('removeSegment', { segment: option })}>
                    <i className="far fa-trash-alt"></i>
                  </SmallRoundedIconButton>
                </>
              )}
            </ButtonGroup>
          }
        />
        <SegmentLeader timelineClassName={'timeline-highlight'} leader={option.leader} />
        {children}
      </SegmentContainer>
      <TimelineVerticalSpacer timelineClassName={'timeline-highlight'} spaces="1" />
    </>
  );
};

const FlexBuilderSegment = ({
  options,
  children,
  openModal,
  timelineId,
}: {
  options: Segment[];
  children: (props: { timelineClassName: string; segment: Segment; sections: AdminSection[] }) => React.ReactNode;
  openModal: (modal: string, params: any) => void;
  timelineId: string;
}) => {
  const [segmentData, setSegmentData] = useState<SegmentData>({
    isOrdering: false,
    hasOrderChanged: false,
    isSaving: false,
    previousOptions: options,
    options: options,
  });

  useEffect(() => {
    setSegmentData(prev => ({
      ...prev,
      previousOptions: options,
      options: options,
    }));
  }, [options]);

  return segmentData.options.map((option, i) => (
    <FlexOption
      key={option.segmentId}
      option={option}
      index={i}
      segmentData={segmentData}
      segment={option}
      setSegmentData={setSegmentData}
      openModal={openModal}
      timelineId={timelineId}
    >
      {children({ timelineClassName: 'timeline-highlight', segment: option, sections: option.sections })}
    </FlexOption>
  ));
};

export default FlexBuilderSegment;
